/**
 * ProfileGeneratorEditor — full-screen orchestrator for one profile card.
 * Owns the useReducer editor state, the image cache, load/save/autosave,
 * keyboard shortcuts, the file picker and the asset browser. Renders the
 * 3-panel layout (Layers / Canvas / Settings) + toolbar, like the spec.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '../../utils/toast';
import { Images } from 'lucide-react';
import { T } from '../../components/em/tokens';
import { useEditorState } from './useEditorState';
import { useImageCache } from './useImageCache';
import { makeImageLayer } from './pgConstants';
import { exportDataURL, makeThumbnail } from './canvasRenderer';
import pgApi from './pgApi';
import EditorCanvas from './pg/EditorCanvas';
import EditorToolbar from './pg/EditorToolbar';
import LayerPanel from './pg/LayerPanel';
import SettingsPanel from './pg/SettingsPanel';
import AssetBrowser from './pg/AssetBrowser';
import ExportDialog from './pg/ExportDialog';
import SaveTemplateDialog from './pg/SaveTemplateDialog';
import './profileGenerator.css';

const fileToDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function ProfileGeneratorEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const { state, stateRef, actions, commit, undo, redo, loadScene, canUndo, canRedo } = useEditorState();
  const imageCache = useImageCache(state.layers);

  const [loading, setLoading] = useState(!isNew || !!params.get('templateId'));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [assetsOpen, setAssetsOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showSaveTpl, setShowSaveTpl] = useState(false);

  const skipDirty = useRef(true);
  const fileRef = useRef(null);
  const replaceMode = useRef(false); // file picker: replace selected vs add new

  const backTo = `${window.location.pathname.split('/profile-generator')[0]}/profile-generator`;

  // ── Load profile or template ─────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    const sceneFrom = (rec) => ({
      layers: rec.layers || [],
      canvasConfig: { ...(rec.canvas_config || {}), zoom: 1 },
      selectedLayerId: (rec.layers && rec.layers[rec.layers.length - 1]?.id) || null,
      profileName: rec.name || 'Untitled profile',
    });
    const tplId = params.get('templateId');
    skipDirty.current = true;
    if (!isNew) {
      pgApi.getProfile(id)
        .then((rec) => { if (alive) { loadScene(sceneFrom(rec)); setLoading(false); } })
        .catch(() => { toast.error('Profile not found'); navigate(backTo); });
    } else if (tplId) {
      pgApi.getTemplate(tplId)
        .then((rec) => { if (alive) { loadScene({ ...sceneFrom(rec), profileName: `${rec.name} profile` }); setLoading(false); } })
        .catch(() => { if (alive) setLoading(false); });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Mark dirty on content changes (skip the load-induced change).
  useEffect(() => {
    if (skipDirty.current) { skipDirty.current = false; return; }
    setDirty(true);
  }, [state.layers, state.canvasConfig, state.profileName]);

  // ── Save ──────────────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    const s = stateRef.current;
    const payload = {
      name: s.profileName?.trim() || 'Untitled profile',
      canvas_config: s.canvasConfig,
      layers: s.layers,
      thumbnail: makeThumbnail({ layers: s.layers, canvasConfig: s.canvasConfig }, imageCache),
    };
    try {
      if (isNew) {
        const { id: newId } = await pgApi.createProfile(payload);
        setDirty(false);
        navigate(`${backTo}/${newId}`, { replace: true });
      } else {
        await pgApi.updateProfile(id, payload);
        setDirty(false);
      }
    } catch {
      toast.error('Could not save profile');
    } finally {
      setSaving(false);
    }
  }, [saving, stateRef, imageCache, isNew, id, navigate, backTo]);

  // Autosave existing profiles every 30s when dirty.
  useEffect(() => {
    if (isNew) return undefined;
    const t = setInterval(() => { if (dirty && !saving) save(); }, 30_000);
    return () => clearInterval(t);
  }, [isNew, dirty, saving, save]);

  // ── Asset library ─────────────────────────────────────────────────────────────
  const openAssets = useCallback(() => {
    setAssetsOpen(true);
    setAssetsLoading(true);
    pgApi.listAssets().then((a) => setAssets(a)).catch(() => {}).finally(() => setAssetsLoading(false));
  }, []);

  const applyAsset = useCallback((asset) => {
    const sel = stateRef.current.layers.find((l) => l.id === stateRef.current.selectedLayerId);
    if (sel && sel.type === 'image') {
      actions.patchLayer(sel.id, { src: asset.url, name: asset.name || sel.name });
    } else {
      const cfg = stateRef.current.canvasConfig;
      const layer = makeImageLayer(asset.url, cfg);
      if (asset.type === 'background') Object.assign(layer, { x: 0, y: 0, width: cfg.width, height: cfg.height, name: 'Background image' });
      actions.addLayer(layer);
    }
  }, [actions, stateRef]);

  // ── Local image upload (the person's photo) ────────────────────────────────────
  const onAddImage = useCallback(() => { replaceMode.current = false; fileRef.current?.click(); }, []);
  const onReplaceImage = useCallback(() => { replaceMode.current = true; fileRef.current?.click(); }, []);
  const onPickFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10 MB'); return; }
    const dataUrl = await fileToDataURL(file);
    const s = stateRef.current;
    const sel = s.layers.find((l) => l.id === s.selectedLayerId);
    if (replaceMode.current && sel && sel.type === 'image') {
      actions.patchLayer(sel.id, { src: dataUrl });
    } else {
      actions.addLayer(makeImageLayer(dataUrl, s.canvasConfig, { name: file.name.replace(/\.[^.]+$/, '') }));
    }
  }, [actions, stateRef]);

  // ── Export ──────────────────────────────────────────────────────────────────────
  const doExport = useCallback(({ type, quality, transparent, filename }) => {
    const s = stateRef.current;
    const url = exportDataURL({ layers: s.layers, canvasConfig: s.canvasConfig }, imageCache, type, quality, transparent);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename || 'profile'}.${type === 'jpg' ? 'jpg' : 'png'}`;
    a.click();
    setShowExport(false);
  }, [stateRef, imageCache]);

  // ── Save as template ──────────────────────────────────────────────────────────
  const doSaveTemplate = useCallback(async ({ name, description, category }) => {
    const s = stateRef.current;
    try {
      await pgApi.createTemplate({
        name, description, category,
        canvas_config: s.canvasConfig, layers: s.layers,
        thumbnail: makeThumbnail({ layers: s.layers, canvasConfig: s.canvasConfig }, imageCache),
        is_system: true,
      });
      toast.success('Template saved');
      setShowSaveTpl(false);
    } catch {
      toast.error('Could not save template');
    }
  }, [stateRef, imageCache]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const ae = document.activeElement;
      const typing = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); save(); return; }
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
      if (typing) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && stateRef.current.selectedLayerId) {
        e.preventDefault(); actions.deleteLayer(stateRef.current.selectedLayerId);
      }
      if (e.key === 'Escape') actions.selectLayer(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save, undo, redo, actions, stateRef]);

  return (
    <div className="pg-editor">
      <EditorToolbar
        profileName={state.profileName}
        onRename={actions.setProfileName}
        onBack={() => navigate(backTo)}
        onSave={save} saving={saving} dirty={dirty}
        onExport={() => setShowExport(true)}
        onSaveTemplate={() => setShowSaveTpl(true)}
        undo={undo} redo={redo} canUndo={canUndo} canRedo={canRedo}
        canvasConfig={state.canvasConfig} actions={actions}
      />

      <div className="pg-body">
        <aside className="pg-left">
          <button type="button" className="pg-lib-btn" onClick={openAssets}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', height: 36, marginBottom: 10, border: `1px solid ${T.border}`, borderRadius: T.rMd, background: T.bgCard, color: T.fg2, cursor: 'pointer', font: `600 12px ${T.font}` }}>
            <Images size={15} /> Asset library
          </button>
          <LayerPanel state={state} actions={actions} onAddImage={onAddImage} />
        </aside>

        <main className="pg-center">
          {loading
            ? <div className="pg-loading" style={{ color: T.fg5, font: `500 14px ${T.font}` }}>Loading…</div>
            : <EditorCanvas state={state} actions={actions} commit={commit} imageCache={imageCache} />}
        </main>

        <aside className="pg-right">
          <SettingsPanel state={state} actions={actions} onOpenAssets={openAssets} onReplaceImage={onReplaceImage} />
        </aside>
      </div>

      <AssetBrowser open={assetsOpen} onClose={() => setAssetsOpen(false)} assets={assets} loading={assetsLoading} onApply={applyAsset} />
      <ExportDialog open={showExport} onClose={() => setShowExport(false)} onExport={doExport} defaultName={state.profileName} />
      <SaveTemplateDialog open={showSaveTpl} onClose={() => setShowSaveTpl(false)} onSave={doSaveTemplate} />

      <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} style={{ display: 'none' }} />
    </div>
  );
}
